// Import some serious stuff
import React, { useLayoutEffect, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import 'react-dates-gte-react-17-21.8.0-version-fixed/initialize';
import { throttle } from 'lodash';
import { RouterProvider, createBrowserRouter, useNavigate, useLocation } from 'react-router-dom';

// Import themes styles & so on
import 'react-table/react-table.css';
import 'react-dates-gte-react-17-21.8.0-version-fixed/lib/css/_datepicker.css';
import 'react-18-image-lightbox/style.css';
import { ThemeProvider } from '@mui/material/styles';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import './index.scss';
import { CookiesProvider } from 'react-cookie';
import logoMainImage from './images/default/demo-logo-main.png';
import logoNavbarImage from './images/default/demo-logo-navbar.png';
import logoIconImage from './images/default/demo-logo-icon.png';
// Import components
import Navbar from './common/components/Navbar/index';
import reducers from './reducers';
import { loadStore, saveStore } from './common/services/LocalStorage';
import Footer from './common/components/Footer/Footer';
import { authMiddleware } from './common/middleware/authMiddleware';
import { clearStorageMiddleware } from './common/middleware/clearStorageMiddleware';
import { initAppConfig } from './common/components/appConfig/actions';
import TokenExpireDialog from './screens/Login/components/TokenExpireDialog';
import APIClient, { configureAPIClient } from './common/services/APIClient';
import { loginToCustomer } from './screens/Login/actions';
import { middleware as idleMiddleware } from './common/services/ReduxIdleMonitor';
import Routes from './Routes';
import { unregister } from './registerServiceWorker';
import { actionStorageMiddleware, createStorageListener } from './storeTabsSync';
import gaPageViewMiddleware from './common/middleware/googleAnalytics';
import { loginToCustomerMiddleware } from './common/middleware/loginToCustomerMiddleware';
import checkDeployMiddleware from './common/middleware/checkDeployMiddleware';
import Alert from './common/components/Alert';
import Notification from './common/components/Notification';
import Confirm from './common/components/Confirm';
import { mainTheme } from './const';
import ScreenBlocker from './common/components/ScreenBlocker';
import { isMobile } from './common/services/isMobile';
import { REDIRECT_QUERY_PARAM_NAME } from './common/services/AuthenticationRules';
import { MENU_TOGGLED_EVENT } from './screens/Reports/components/DrillDownChart/component';

// to save state to localstorage at least once in second
const throttleInterval = 1000;

// Load store state from localstorage
const preloadedState = loadStore();

const store = configureStore({
  reducer: reducers(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(
      idleMiddleware,
      authMiddleware,
      checkDeployMiddleware,
      clearStorageMiddleware,
      actionStorageMiddleware,
      gaPageViewMiddleware,
      loginToCustomerMiddleware,
    ),
  preloadedState,
});

createStorageListener(store);

// Set interceptor
configureAPIClient(store);

store.subscribe(
  throttle(() => {
    saveStore(store.getState());
  }, throttleInterval),
);

function Content() {
  const navigate = useNavigate();
  const location = useLocation();

  const [prevPathname, setPrevPathname] = useState('');

  useLayoutEffect(() => {
    APIClient.get('/customers/settings')
      .then((resp) => {
        const clearConfig = { ...resp.data };
        const configObj = resp.data;

        document.title = `${configObj.customerName} - IoT.ON`;
        configObj.logoMain = configObj.logoMain ? configObj.logoMain : logoMainImage;
        configObj.logoNavbar = configObj.logoNavbar ? configObj.logoNavbar : logoNavbarImage;
        configObj.logoIcon = configObj.logoIcon ? configObj.logoIcon : logoIconImage;

        store.dispatch(initAppConfig(configObj, clearConfig));
      })
      .catch(() => {
        const path = '/pageNotFound';

        navigate(path, { replace: true });
      });
  }, []);

  useEffect(() => {
    const { pathname } = location;

    if (pathname && pathname.includes('/login/customer')) {
      store.dispatch(loginToCustomer(window.location.search));
    }

    store.dispatch({ type: 'HISTORY_LOCATION_CHANGE', pathname, previousPathname: prevPathname });
    setPrevPathname(pathname);

    const redirectPath = localStorage.getItem(REDIRECT_QUERY_PARAM_NAME);

    if (redirectPath) {
      setTimeout(() => navigate(redirectPath, { replace: true }), 0);
      localStorage.removeItem(REDIRECT_QUERY_PARAM_NAME);
    }
  }, [location]);

  const [openedMenu, setOpenedMenu] = useState(false);

  const handleToggleMenu = (value) => {
    setOpenedMenu(value);
    setTimeout(() => window.dispatchEvent(new Event(MENU_TOGGLED_EVENT)), 500);
  };

  return (
    <div className={`main-content-wrapper${isMobile ? ' mobile' : ''}`}>
      <Navbar open={openedMenu} onSetOpen={handleToggleMenu} />
      <div id="content" open={openedMenu}>
        <Routes />
      </div>
    </div>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);

const Root = () => (
  <div id="root-child">
    <Content />
    {window.location.pathname !== '/pageNotFound' && <Footer />}
    <TokenExpireDialog />
    <Alert />
    <Notification />
    <Confirm />
    <ScreenBlocker />
  </div>
);

const router = createBrowserRouter([{ path: '*', Component: Root }]);

root.render(
  <CookiesProvider>
    <Provider store={store}>
      <ThemeProvider theme={mainTheme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <RouterProvider router={router} />
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  </CookiesProvider>,
);

unregister();
